import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Video,
  Car,
  FileText,
  AlertTriangle,
  Download,
  Image,
  User,
  Eye,
  Activity,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Upload,
  Loader2,
  Play,
  ExternalLink,
  Brain,
  RefreshCw,
  X,
  Lock,
  Check,
} from 'lucide-react';
import { useRef } from 'react';
import { useAuthStore } from '@/stores/auth-store';

import { Header } from '@/components/layout/header';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import {
  formatDate,
  getInitials,
  cn,
  formatDateTime,
  formatFileSize,
  downloadFile,
  convertToTitleCase,
} from '@/lib/utils';
import { useClaim, useUpdateClaimStatus, useReplaceDocument } from '@/hooks/use-claims';
import { useTriggerTrinityCheck } from '@/hooks/use-trinity';
import { useCreateVideoRoom, useSessionDeceptionScore } from '@/hooks/use-video';
import { useReverseGeocode } from '@/hooks/use-location';
import { useToast } from '@/hooks/use-toast';
import { InfoTooltip } from '@/components/ui/tooltip';
import { useHasPermission, PERMISSIONS } from '@/lib/permissions';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import { useMemo } from 'react';
import { useLayout } from '@/components/layout';
import {
  AssessmentMode,
  ASSESSMENT_MODE_LABELS,
  canDecideClaim,
  describeLocation,
} from '@tci/shared-types';
import { getCategoryConfig } from '@/lib/category-config';
import { PropertyDetailsPanel } from '@/components/claims/non-motor/property-details-panel';
import { EvidenceChecklistCard } from '@/components/claims/non-motor/evidence-checklist-card';
import { QuantumWorksheetPanel } from '@/components/claims/quantum-worksheet';
import { ReportPanel } from '@/components/claims/report-panel';
import { FraudSignalsCard } from '@/components/claims/non-motor/fraud-signals-card';
import { SlaPanel } from '@/components/claims/sla-panel';
import { IdentityControl } from '@/components/claims/identity-control';
import { ScheduleAssessment } from '@/components/claims/schedule-assessment';
import { SiteVisitCard } from '@/components/claims/site-visit-card';
import { FeeNotePanel } from '@/components/claims/fee-note-panel';
import { SignatureControls } from '@/components/claims/non-motor/signature-controls';

const statusConfig: Record<
  string,
  {
    label: string;
    variant: 'default' | 'secondary' | 'success' | 'warning' | 'info' | 'destructive';
  }
> = {
  SUBMITTED: { label: 'Submitted', variant: 'secondary' },
  ASSIGNED: { label: 'Assigned', variant: 'info' },
  SCHEDULED: { label: 'Scheduled', variant: 'info' },
  IN_ASSESSMENT: { label: 'In Assessment', variant: 'warning' },
  REPORT_PENDING: { label: 'Report Pending', variant: 'warning' },
  APPROVED: { label: 'Approved', variant: 'success' },
  REJECTED: { label: 'Rejected', variant: 'destructive' },
  CLOSED: { label: 'Closed', variant: 'secondary' },
};

const SessionChart = ({
  sessionId,
  deceptionData,
  summary,
}: {
  sessionId: string;
  deceptionData: any[];
  summary?: any;
}) => {
  const { data: remoteSummary, isLoading: isDeceptionLoading } = useSessionDeceptionScore(
    sessionId,
    !summary // Only enable query if summary is missing
  );

  const deception = summary || remoteSummary;

  const chartData = useMemo(() => {
    if (!deceptionData || deceptionData.length === 0) return [];

    return [...deceptionData]
      .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
      .map(d => ({
        time: new Date(d.createdAt).toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        }),
        deception: d.deception,
        voice: d.voice,
        visual: d.visual,
        expression: d.expression,
      }));
  }, [deceptionData]);

  if (isDeceptionLoading && !deception) {
    return (
      <div className="h-40 flex items-center justify-center text-xs text-muted-foreground animate-pulse">
        Loading metrics...
      </div>
    );
  }

  if (!deceptionData || deceptionData.length === 0) {
    return (
      <div className="h-40 flex flex-col items-center justify-center text-center text-xs text-muted-foreground bg-muted/40 rounded-lg border border-dashed border-slate-200 dark:border-slate-800">
        <Activity className="h-5 w-5 mb-2 opacity-20" />
        No risk data captured for this session.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="h-40 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
            <XAxis
              dataKey="time"
              fontSize={9}
              tickMargin={10}
              axisLine={false}
              tickLine={false}
              hide
            />
            <YAxis hide domain={[0, 100]} />
            <Tooltip
              formatter={(value: any, name: string) => [`${parseFloat(value).toFixed(2)}%`, name]}
              contentStyle={{
                backgroundColor: '#fff',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '10px',
                color: '#000',
                textAlign: 'center',
                boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
              }}
              labelStyle={{ color: '#000', fontWeight: 'bold', marginBottom: '4px' }}
              itemStyle={{ padding: '0 2px' }}
            />
            <Line
              type="monotone"
              dataKey="deception"
              stroke="#A855F7"
              strokeWidth={2}
              dot={false}
              name="Deception Score"
            />
            <Line
              type="monotone"
              dataKey="voice"
              stroke="#72B0F2"
              strokeWidth={1.5}
              dot={false}
              strokeOpacity={0.4}
              name="Voice Stress"
            />
            <Line
              type="monotone"
              dataKey="visual"
              stroke="#2EE797"
              strokeWidth={1.5}
              dot={false}
              strokeOpacity={0.4}
              name="Visual Behavior"
            />
            <Line
              type="monotone"
              dataKey="expression"
              stroke="#E884B6"
              strokeWidth={1.5}
              dot={false}
              strokeOpacity={0.4}
              name="Expression Measurement"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const NORMALIZABLE_TYPES = [
  'MYKAD_FRONT',
  'NRIC',
  'VEHICLE_REG_CARD',
  'POLICE_REPORT',
  'REPAIR_QUOTATION',
  'DAMAGE_PHOTO',
  'POLICY_DOCUMENT',
];

export function ClaimDetailPage() {
  const { id: claimId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isMobile, currentWidth } = useLayout();

  const { data: claim, isLoading, refetch: refetchClaim } = useClaim(claimId || '');
  const updateStatus = useUpdateClaimStatus(claimId || '');
  const createVideoRoom = useCreateVideoRoom();
  const [magicLink, setMagicLink] = useState<string | null>(null);
  const [isNotifying, setIsNotifying] = useState(false);
  const triggerTrinity = useTriggerTrinityCheck();
  const [isTrinityConfirmOpen, setIsTrinityConfirmOpen] = useState(false);
  const replaceDocument = useReplaceDocument(claimId || '');
  const [isReplaceConfirmOpen, setIsReplaceConfirmOpen] = useState(false);
  const [selectedDocForReplace, setSelectedDocForReplace] = useState<any>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [expandedSessions, setExpandedSessions] = useState<Set<string>>(new Set());

  // Permission checks
  const canApprove = useHasPermission(PERMISSIONS.CLAIMS_APPROVE);
  const canManageUsers = useHasPermission(PERMISSIONS.USERS_MANAGE);
  const canConductVideo = useHasPermission(PERMISSIONS.VIDEO_CONDUCT);
  const canEscalateSIU = useHasPermission(PERMISSIONS.SIU_ESCALATE);
  const canInvestigateSIU = useHasPermission(PERMISSIONS.SIU_INVESTIGATE);
  const canViewUnmaskedPII = useHasPermission(PERMISSIONS.PII_VIEW_UNMASKED);
  const canViewFinancials = !useHasPermission(PERMISSIONS.CLAIMS_VIEW_BASIC); // Support only has basic view
  const canEdit = useHasPermission(PERMISSIONS.CLAIMS_EDIT);

  const currentUser = useAuthStore(state => state.user);
  const canManageSessions =
    currentUser && ['ADJUSTER', 'FIRM_ADMIN', 'SUPER_ADMIN'].includes(currentUser.role);

  const latestClaimantClientInfo = useMemo(() => {
    if (!claim?.sessions) return null;

    const allClientInfos = (claim.sessions as any[]).flatMap((s: any) => s.clientInfos || []);
    const claimantInfos = allClientInfos.filter(
      (info: any) => info.metadata?.claimantId === claim.claimant?.id
    );

    if (claimantInfos.length === 0) return null;

    return claimantInfos.sort(
      (a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    )[0];
  }, [claim]);

  const { data: locationData, isLoading: isLocationLoading } = useReverseGeocode(
    latestClaimantClientInfo?.latitude || null,
    latestClaimantClientInfo?.longitude || null
  );

  // Pagination states
  const [docPage, setDocPage] = useState(1);
  const [timelinePage, setTimelinePage] = useState(1);
  const ITEMS_PER_PAGE = 5;

  const [modifier, setModifier] = useState<string>('Ctrl');

  // Detect OS for modifier key
  useEffect(() => {
    if (typeof window !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform)) {
      setModifier('⌘');
    } else {
      setModifier('Ctrl');
    }
  }, []);

  // Reset pagination when claim changes
  useEffect(() => {
    setDocPage(1);
    setTimelinePage(1);
  }, [claimId]);

  // Shortcut key controls
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check if Ctrl or Command key is pressed
      if (e.ctrlKey || e.metaKey) {
        if (e.key === 'Enter') {
          e.preventDefault();
          if (claim && canDecideClaim(claim.status) && canApprove) {
            handleUpdateStatus('APPROVED');
          }
        } else if (e.key === 'Backspace') {
          e.preventDefault();
          if (claim && canDecideClaim(claim.status) && canApprove) {
            handleUpdateStatus('REJECTED');
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [claim?.status, claimId]);

  // Initialize expanded sessions with the most recent one
  useEffect(() => {
    if (claim?.sessions && claim.sessions.length > 0 && expandedSessions.size === 0) {
      const sorted = [...claim.sessions].sort(
        (a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      setExpandedSessions(new Set([sorted[0].id]));
    }
  }, [claim?.sessions]);

  const toggleSession = (id: string) => {
    const next = new Set(expandedSessions);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setExpandedSessions(next);
  };

  const handleUpdateStatus = async (status: string) => {
    if (!claimId || updateStatus.isPending) return;

    try {
      await updateStatus.mutateAsync(status as any);
      toast({
        title: 'Success',
        description: `Claim status updated to ${status}.`,
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: 'Failed to update claim status.',
        variant: 'destructive',
      });
    }
  };

  const activeVideoSession = claim?.sessions?.find(
    s => s.status === 'WAITING' || s.status === 'SCHEDULED' || s.status === 'IN_PROGRESS'
  );

  const handleStartVideoAssessment = async () => {
    if (!claimId) return;
    setIsNotifying(true);

    try {
      let sessionId: string;

      // If there's already an active session, use it; otherwise create one
      if (activeVideoSession) {
        sessionId = activeVideoSession.id;
      } else {
        const session = await createVideoRoom.mutateAsync(claimId);
        sessionId = session.sessionId;
      }

      // Generate magic link for claimant (in production, this would send SMS)
      const devLink = `http://localhost:4001/video/${sessionId}`;
      setMagicLink(devLink);

      // Auto-copy to clipboard for easy testing
      await navigator.clipboard.writeText(devLink);

      toast({
        title: 'Live Session Started',
        description: 'Magic link copied to clipboard! Joining video room...',
      });

      // Small delay to show the magic link before navigating
      await new Promise(resolve => setTimeout(resolve, 500));

      // Navigate adjuster to video call
      navigate(`/claims/${claimId}/live-session/${sessionId}`);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: 'Failed to start live session. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsNotifying(false);
    }
  };

  const handleTriggerTrinity = async () => {
    if (!claimId || triggerTrinity.isPending) return;

    try {
      await triggerTrinity.mutateAsync(claimId);
      setIsTrinityConfirmOpen(false);
      toast({
        title: 'Analysis Triggered',
        description: 'AI extraction and cross-check analysis have been queued for all documents.',
      });
    } catch (error: any) {
      toast({
        title: 'Analysis Failed',
        description: error.response?.data?.message || 'Failed to trigger document analysis.',
        variant: 'destructive',
      });
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: 'File too large',
          description: 'The document file size must be less than 5MB.',
          variant: 'destructive',
        });
        e.target.value = '';
        return;
      }

      // Validate file type (PDF or Image)
      if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
        toast({
          title: 'Invalid file type',
          description: 'Only PDF and image files are allowed.',
          variant: 'destructive',
        });
        e.target.value = '';
        return;
      }

      setPendingFile(file);
    }
    // Reset input
    e.target.value = '';
  };

  const handleConfirmReplace = async () => {
    if (!selectedDocForReplace || !pendingFile) return;

    try {
      await replaceDocument.mutateAsync({
        documentId: selectedDocForReplace.id,
        file: pendingFile,
      });
      setIsReplaceConfirmOpen(false);
      setSelectedDocForReplace(null);
      setPendingFile(null);
      toast({
        title: 'Document Replaced',
        description:
          'The document has been successfully replaced, and the analysis has been rerun.',
      });
    } catch (error: any) {
      toast({
        title: 'Replacement Failed',
        description: error.response?.data?.message || 'Failed to replace document.',
        variant: 'destructive',
      });
    }
  };

  if (isLoading || !claim) {
    return (
      <div className="flex flex-col h-full space-y-6 p-6">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-4 w-64" />
          </div>
          <div className="flex gap-2">
            <Skeleton className="h-9 w-24" />
            <Skeleton className="h-9 w-24" />
          </div>
        </div>
        <div className="flex flex-col-reverse lg:grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-6 lg:order-1">
            <Skeleton className="h-64 w-full" />
            <Skeleton className="h-48 w-full" />
            <Skeleton className="h-48 w-full" />
          </div>
          <div className="space-y-6">
            <Skeleton className="h-40 w-full" />
            <Skeleton className="h-96 w-full" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <Header
        title={
          <span className="flex items-center gap-3">
            {claim.claimNumber}
            {!isMobile && (
              <Badge variant={statusConfig[claim.status].variant}>
                {convertToTitleCase(statusConfig[claim.status].label)}
              </Badge>
            )}
          </span>
        }
        description={`${claim.claimant?.fullName || claim.claimantId} • ${claim.category === 'MOTOR' ? convertToTitleCase(claim.claimType ?? 'Motor') : getCategoryConfig(claim.category).label}`}
      >
        <div className="flex items-center gap-3">
          {canApprove && (
            <>
              <Button
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700 h-10 px-4 flex-col items-center justify-center sm:min-w-[120px]"
                onClick={() => handleUpdateStatus('APPROVED')}
                disabled={!canDecideClaim(claim.status) || updateStatus.isPending}
              >
                {updateStatus.isPending && updateStatus.variables === 'APPROVED' ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    <span className="font-bold">Approving...</span>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-2">
                      {currentWidth > 430 ? (
                        <span className="font-bold">Approve</span>
                      ) : (
                        <span className="font-bold">
                          <Check className="h-4 w-4" />
                        </span>
                      )}
                    </div>
                    {!isMobile && (
                      <span className="text-[9px] opacity-80 font-normal leading-none font-mono tracking-tight">
                        {modifier} + Enter
                      </span>
                    )}
                  </>
                )}
              </Button>

              <Button
                variant="destructive"
                size="sm"
                className="h-10 px-4 flex-col items-center justify-center sm:min-w-[120px]"
                onClick={() => handleUpdateStatus('REJECTED')}
                disabled={!canDecideClaim(claim.status) || updateStatus.isPending}
              >
                {updateStatus.isPending && updateStatus.variables === 'REJECTED' ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    <span className="font-bold">Rejecting...</span>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-2">
                      {currentWidth > 430 ? (
                        <span className="font-bold">Reject</span>
                      ) : (
                        <span className="font-bold">
                          <X className="h-4 w-4" />
                        </span>
                      )}
                    </div>
                    {!isMobile && (
                      <span className="text-[9px] opacity-80 font-normal leading-none font-mono tracking-tight">
                        {modifier} + Backspace
                      </span>
                    )}
                  </>
                )}
              </Button>
            </>
          )}
        </div>
      </Header>

      <div className="flex-1 overflow-auto p-6">
        <div className="flex flex-col-reverse lg:grid gap-6 lg:grid-cols-3">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Incident Details */}
            <Card>
              <CardHeader className="flex flex-col sm:flex-row items-center justify-between pb-3">
                <CardTitle>Incident Details</CardTitle>
                <span className="text-xs text-muted-foreground">
                  Updated: {formatDate(claim.updatedAt)}
                </span>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="flex items-start gap-3">
                    <div>
                      <p className="text-xs font-medium">Incident Date</p>
                      <p className="text-xs text-muted-foreground">
                        {formatDate(claim.incidentDate)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div>
                      <p className="text-xs font-medium">
                        {claim.category === 'TRAVEL' ? 'Destination' : 'Location'}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {describeLocation(claim.incidentLocation) ?? 'Not recorded'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-3 pt-2">
                  <div className="flex-1">
                    <p className="text-xs font-medium">Description</p>
                    <p className="text-xs text-muted-foreground mt-1">{claim.description}</p>
                  </div>
                </div>

                {/* Police Report */}
                {claim.policeReportDate && (
                  <div className="pt-4 border-t">
                    <div className="flex flex-col sm:flex-row items-center justify-between">
                      <p className="text-lg font-medium mb-2">Police Report</p>
                      <span className="text-xs text-muted-foreground mb-3">
                        Reported: {formatDate(claim.updatedAt)}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                      <div className="flex flex-col justify-between">
                        <span className="text-xs font-medium">Report Number:</span>
                        <span className="text-xs text-muted-foreground">
                          {claim?.policeReportNumber || 'N/A'}
                        </span>
                      </div>
                      <div className="flex flex-col justify-between">
                        <span className="text-xs font-medium">Police Station:</span>
                        <span className="text-xs text-muted-foreground">
                          {claim?.policeStation || 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Other Party Details (for TPPD claims) */}
                {claim.otherParty && Object.keys(claim.otherParty).length > 0 && (
                  <div className="pt-4 border-t">
                    <p className="text-sm font-medium mb-2">Other Party Details</p>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      {(claim.otherParty as any).name && (
                        <div className="flex justify-between">
                          <span className="text-xs font-medium">Name:</span>
                          <span className="text-xs text-muted-foreground">
                            {String((claim.otherParty as any).name)}
                          </span>
                        </div>
                      )}
                      {(claim.otherParty as any).vehiclePlate && (
                        <div className="flex justify-between">
                          <span className="text-xs font-medium">Vehicle:</span>
                          <span className="text-xs text-muted-foreground">
                            {String((claim.otherParty as any).vehiclePlate)}
                          </span>
                        </div>
                      )}
                      {(claim.otherParty as any).insurerName && (
                        <div className="flex justify-between col-span-2">
                          <span className="text-xs font-medium">Insurer:</span>
                          <span className="text-xs text-muted-foreground">
                            {String((claim.otherParty as any).insurerName)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {canViewFinancials && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">Financials & Estimates</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-1">
                      <p className="text-sm font-medium">Estimated Loss</p>
                      <p className="text-xl font-bold">
                        {claim.estimatedLossAmount
                          ? `RM ${claim.estimatedLossAmount.toLocaleString()}`
                          : 'Pending'}
                      </p>
                    </div>
                    {/* "Repair cost" is a motor idea. A bag is not repaired and a
                        cancelled trip has nothing to repair; the assessed figure
                        lives on the quantum worksheet, which is shown in full
                        below. Only motor keeps this field. */}
                    {claim.category === 'MOTOR' && (
                      <div className="space-y-1">
                        <p className="text-sm font-medium">Repair Cost (Final)</p>
                        <p className="text-xl font-bold text-muted-foreground">
                          {claim.estimatedRepairCost
                            ? `RM ${claim.estimatedRepairCost.toLocaleString()}`
                            : 'Pending'}
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="grid gap-4 md:grid-cols-2 pt-4 border-t">
                    {/* SST is charged on the firm's fee, not on the claim — it
                        belongs on the fee note and is shown there. Printing an
                        empty one here invited the reading that a claimant pays
                        service tax on their own loss. */}
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Policy excess</span>
                      <span className="text-sm font-medium">
                        {claim.excessAmount ? `RM ${claim.excessAmount.toLocaleString()}` : 'N/A'}
                      </span>
                    </div>
                  </div>
                  {claim.approvedAmount && (
                    <div className="pt-4 border-t">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-green-700">Approved Payout</span>
                        <span className="text-xl font-bold text-green-700">
                          RM {claim.approvedAmount.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Quantum — sits beside the financials it produces, not in a
                separate screen: the figure and the estimates it supersedes
                should be read together. */}
            {claimId && <QuantumWorksheetPanel claimId={claimId} />}

            {/* The work product itself: written here, signed, then handed
                back. It sits below quantum because the figure is what the
                report reasons about. */}
            {claimId && <ReportPanel claimId={claimId} claimStatus={claim.status} />}

            {/* Documents */}
            <Card>
              <CardHeader className="flex flex-col sm:flex-row gap-3 items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  Documents
                  <InfoTooltip
                    content="View"
                    direction="top"
                    fontSize="text-[11px]"
                    trigger={
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        onClick={() => navigate(`/documents/${claimId}`)}
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    }
                  />
                </CardTitle>
                <div className="flex items-center gap-2">
                  {claim.documents && claim.documents.length > 0 && (
                    <InfoTooltip
                      content="Rerun Analysis"
                      direction="top"
                      fontSize="text-[11px]"
                      trigger={
                        <Button
                          variant="ghost"
                          size="sm"
                          className={cn(
                            'h-6 w-6 p-0 rounded-full bg-muted hover:bg-emerald-50 hover:text-primary transition-colors',
                            triggerTrinity.isPending && 'animate-pulse text-primary bg-emerald-50'
                          )}
                          onClick={e => {
                            e.stopPropagation();
                            setIsTrinityConfirmOpen(true);
                          }}
                          disabled={triggerTrinity.isPending}
                        >
                          {triggerTrinity.isPending ? (
                            <Loader2 className="h-2.5 w-2.5 animate-spin" />
                          ) : (
                            <Play className="h-2.5 w-2.5 fill-current" />
                          )}
                        </Button>
                      }
                    />
                  )}
                  {claim.trinityChecks && claim.trinityChecks.length > 0 && (
                    <InfoTooltip
                      content={
                        <div className="space-y-2 max-w-[450px]">
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-bold text-base">True Claim Intelligence</span>
                            <Badge
                              variant={
                                claim.trinityChecks[0].score > 70
                                  ? 'destructive'
                                  : claim.trinityChecks[0].score > 30
                                    ? 'warning'
                                    : 'success'
                              }
                            >
                              {claim.trinityChecks[0].score || 0}%
                            </Badge>
                          </div>
                          {(claim.trinityChecks[0] as any)?.reasoningInsights?.recommendation && (
                            <div className="mt-2">
                              <Badge variant="secondary" className="flex items-start gap-2 w-fit">
                                <span className="font-normal whitespace-nowrap">
                                  Recommended Action:
                                </span>
                                <span className="font-semibold">
                                  {convertToTitleCase(
                                    (claim.trinityChecks[0] as any).reasoningInsights.recommendation
                                  )}
                                </span>
                              </Badge>
                            </div>
                          )}
                          <p className="text-justify text-xs text-muted-foreground p-2">
                            {(claim.trinityChecks[0] as any)?.reasoning ||
                              'No detailed reasoning available.'}
                          </p>
                        </div>
                      }
                      direction="top"
                      fontSize="text-[11px]"
                      trigger={
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 rounded-full bg-muted hover:bg-emerald-50 text-emerald-600 hover:text-primary transition-colors"
                          onClick={() => navigate(`/documents/${claimId}`)}
                        >
                          <Brain className="h-4 w-4" />
                        </Button>
                      }
                    />
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {claim.documents && claim.documents.length > 0 ? (
                  <div className="space-y-4">
                    <div data-horizontal="true" className="overflow-x-auto custom-scrollbar pb-2">
                      <div className="min-w-max space-y-2">
                        {(claim.documents || [])
                          .slice()
                          .sort((a: any, b: any) => {
                            const dateA = new Date(a.metadata?.replacedAt || a.createdAt).getTime();
                            const dateB = new Date(b.metadata?.replacedAt || b.createdAt).getTime();
                            return dateA - dateB;
                          })
                          .slice((docPage - 1) * ITEMS_PER_PAGE, docPage * ITEMS_PER_PAGE)
                          .map((doc: any) => (
                            <div
                              key={doc.id}
                              className="flex items-center justify-between p-3 bg-muted/50 rounded-lg gap-4 whitespace-nowrap"
                            >
                              <div className="flex items-center gap-3 shrink-0">
                                {doc.type === 'DAMAGE_PHOTO' ? (
                                  <Image className="h-4 w-4 text-muted-foreground shrink-0" />
                                ) : (
                                  <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                                )}
                                <div>
                                  <p className="text-sm font-medium whitespace-nowrap">
                                    {doc.filename}
                                  </p>
                                  <div className="flex items-center gap-2 text-xs text-muted-foreground whitespace-nowrap">
                                    <span>{doc.type.replace(/_/gi, ' ')}</span>
                                    <span>•</span>
                                    <span>{formatFileSize(doc.fileSize)}</span>
                                    <span>•</span>
                                    <span>
                                      {formatDateTime(doc.metadata?.replacedAt || doc.createdAt)}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-1 shrink-0">
                                {/* Signature lifecycle status + actions.
                                    Only consent statements get the
                                    "Request" button; other types still
                                    show a status badge if non-default. */}
                                <SignatureControls
                                  doc={doc}
                                  claimId={claim.id}
                                  canRequest={doc.type === 'SIGNED_STATEMENT'}
                                />
                                <InfoTooltip
                                  content="View"
                                  direction="top"
                                  fontSize="text-[11px]"
                                  trigger={
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-8 w-8 p-0"
                                      onClick={() => {
                                        if (doc.storageUrl) {
                                          window.open(doc.storageUrl, '_blank');
                                        }
                                      }}
                                    >
                                      <Eye className="h-4 w-4" />
                                    </Button>
                                  }
                                />
                                <InfoTooltip
                                  content="Download"
                                  direction="top"
                                  fontSize="text-[11px]"
                                  trigger={
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-8 w-8 p-0"
                                      onClick={() => {
                                        if (doc.storageUrl) {
                                          downloadFile(doc.storageUrl, doc.filename);
                                        }
                                      }}
                                    >
                                      <Download className="h-4 w-4" />
                                    </Button>
                                  }
                                />
                                <InfoTooltip
                                  content="Replace"
                                  direction="top"
                                  fontSize="text-[11px]"
                                  trigger={
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-8 w-8 p-0 hover:bg-amber-600 transition-colors group"
                                      onClick={() => {
                                        setSelectedDocForReplace(doc);
                                        setIsReplaceConfirmOpen(true);
                                      }}
                                      disabled={replaceDocument.isPending}
                                    >
                                      {replaceDocument.isPending &&
                                      selectedDocForReplace?.id === doc.id ? (
                                        <Loader2 className="h-4 w-4 animate-spin text-primary" />
                                      ) : (
                                        <RefreshCw className="h-4 w-4 text-amber-600 group-hover:text-white transition-colors" />
                                      )}
                                    </Button>
                                  }
                                />
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>

                    {/* Documents Pagination Controls */}
                    {claim.documents.length > ITEMS_PER_PAGE && (
                      <div className="flex items-center justify-center gap-3 pt-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => setDocPage(p => Math.max(1, p - 1))}
                          disabled={docPage === 1}
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <span className="text-xs text-muted-foreground font-medium">
                          Page {docPage} of{' '}
                          {Math.ceil((claim.documents?.length || 0) / ITEMS_PER_PAGE)}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() =>
                            setDocPage(p =>
                              Math.min(
                                Math.ceil((claim.documents?.length || 0) / ITEMS_PER_PAGE),
                                p + 1
                              )
                            )
                          }
                          disabled={
                            docPage >= Math.ceil((claim.documents?.length || 0) / ITEMS_PER_PAGE)
                          }
                        >
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center justify-center p-8 text-muted-foreground border-2 border-dashed rounded-lg">
                    <p className="text-sm">No documents uploaded yet.</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Timeline (Sessions Analysis) */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  Timeline
                  <InfoTooltip
                    content="View"
                    direction="top"
                    fontSize="text-[11px]"
                    trigger={
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        onClick={() => navigate(`/sessions`)}
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    }
                  />
                </CardTitle>
                <InfoTooltip
                  title="Metrics Analysis"
                  direction="top"
                  variant="light"
                  iconSize={4}
                  fontSize="text-xs"
                  className="scale-110 mt-1"
                  contentClassName="max-w-[450px]"
                  content={
                    <div className="flex gap-8 items-start">
                      <section className="flex-1 space-y-3 p-2">
                        <p className="text-muted-foreground font-medium mb-3">
                          This section shows a timeline of past sessions with their deception
                          metrics.
                        </p>
                        <ul className="space-y-2.5 mt-2 text-muted-foreground">
                          <li>
                            <strong className="text-foreground block mb-0.5">Voice Stress</strong>
                            Analyzes vocal patterns that may indicate stress, tension, or emotional
                            load during speech. The scoring is calculated by normalizing
                            measurements against industry baselines:
                            <div className="space-y-3 font-mono text-[11px] bg-muted p-3 rounded-lg border border-border shadow-sm text-foreground/80 leading-relaxed pt-1 pb-3">
                              <p className="mt-2 text-primary font-semibold">
                                Score = (0.25×Jitter + 0.25×Shimmer + 0.2×PitchSD + 0.1×HNR)²
                              </p>
                            </div>
                          </li>
                          <li>
                            <strong className="text-foreground block mb-0.5">
                              Visual Behavior:
                            </strong>
                            Tracks facial and eye-related behaviors that can reflect attention,
                            comfort, or cognitive effort. The scoring is calculated by aggregate
                            risk points:
                            <div className="space-y-3 font-mono text-[11px] bg-muted p-3 rounded-lg border border-border shadow-sm text-foreground/80 leading-relaxed pt-1 pb-3">
                              <p className="mt-2 text-primary font-semibold">
                                Score = BlinkRateDev(40%) + LipTension(40%) + BlinkDurDev(20%)
                              </p>
                            </div>
                          </li>
                          <li>
                            <strong className="text-foreground block mb-0.5">
                              Expression Measurement:
                            </strong>
                            Detects emotional and cognitive states based on facial expressions. The
                            scoring is calculated by peak fraud-linked emotion with baseline
                            correction:
                            <div className="space-y-3 font-mono text-[11px] bg-muted p-3 rounded-lg border border-border shadow-sm text-foreground/80 leading-relaxed pt-1 pb-3">
                              <p className="mt-2 text-primary font-semibold">
                                Score = Max(FraudEmotions) - 10% Noise Threshold
                              </p>
                            </div>
                          </li>
                        </ul>

                        <div className="pt-2 mt-5 border-t border-border">
                          <p className="text-foreground leading-normal">
                            The <span className="font-semibold">Deception Score</span> is the
                            balanced average of the three pillars above, with each category weighted
                            equally at one-third.
                          </p>
                        </div>
                      </section>
                    </div>
                  }
                />
              </CardHeader>

              <CardContent className="space-y-8">
                {claim.sessions && claim.sessions.length > 0 ? (
                  <div className="space-y-6">
                    <div className="space-y-8">
                      {/* Sort and paginate sessions */}
                      {[...claim.sessions]
                        .sort(
                          (a: any, b: any) =>
                            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
                        )
                        .slice((timelinePage - 1) * ITEMS_PER_PAGE, timelinePage * ITEMS_PER_PAGE)
                        .map((session: any) => (
                          <div
                            key={session.id}
                            className="space-y-4 border-b pb-8 last:border-0 last:pb-0"
                          >
                            <div
                              className="flex flex-col sm:flex-row gap-3 items-center justify-between cursor-pointer group"
                              onClick={() => toggleSession(session.id)}
                            >
                              <div className="flex items-center gap-2">
                                <div
                                  className={cn(
                                    'w-2 h-2 rounded-full',
                                    session.status === 'COMPLETED'
                                      ? 'bg-emerald-500'
                                      : 'bg-blue-500'
                                  )}
                                />
                                <h4 className="text-sm font-semibold transition-colors">
                                  Session: {formatDateTime(session.createdAt)}
                                </h4>
                              </div>
                              <div className="flex items-center gap-3">
                                <Badge
                                  variant="outline"
                                  className={cn(
                                    'text-[10px]',
                                    session.roomUrl
                                      ? 'border-indigo-200 bg-indigo-50 text-indigo-700'
                                      : 'border-slate-200 bg-slate-50 text-slate-600'
                                  )}
                                >
                                  {session.roomUrl ? 'Live' : 'Upload'}
                                </Badge>
                                <Badge
                                  variant={session.status === 'COMPLETED' ? 'default' : 'secondary'}
                                  className="text-[10px]"
                                >
                                  {convertToTitleCase(session.status)}
                                </Badge>
                                {expandedSessions.has(session.id) ? (
                                  <ChevronUp className="h-4 w-4 text-slate-400" />
                                ) : (
                                  <ChevronDown className="h-4 w-4 text-slate-400" />
                                )}
                              </div>
                            </div>

                            {expandedSessions.has(session.id) && (
                              <div className="animate-in fade-in slide-in-from-top-2 duration-300">
                                <SessionChart
                                  sessionId={session.id}
                                  deceptionData={session.deceptionData || []}
                                  summary={session.summary}
                                />
                              </div>
                            )}
                          </div>
                        ))}
                    </div>

                    {/* Timeline Pagination Controls */}
                    {claim.sessions.length > ITEMS_PER_PAGE && (
                      <div className="flex items-center justify-center gap-3 pt-4 border-t">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => setTimelinePage(p => Math.max(1, p - 1))}
                          disabled={timelinePage === 1}
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <span className="text-xs text-muted-foreground font-medium">
                          Page {timelinePage} of{' '}
                          {Math.ceil((claim.sessions?.length || 0) / ITEMS_PER_PAGE)}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() =>
                            setTimelinePage(p =>
                              Math.min(
                                Math.ceil((claim.sessions?.length || 0) / ITEMS_PER_PAGE),
                                p + 1
                              )
                            )
                          }
                          disabled={
                            timelinePage >=
                            Math.ceil((claim.sessions?.length || 0) / ITEMS_PER_PAGE)
                          }
                        >
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-muted-foreground border-2 border-dashed rounded-lg">
                    <Activity className="h-8 w-8 mb-2 opacity-20" />
                    <p className="text-sm text-center">
                      No analysis sessions found for this claim.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <SlaPanel claimId={claimId} />
            <FeeNotePanel claimId={claimId} />

            {/* Session button */}
            {canManageSessions &&
              (() => {
                /* The assessment-mode router decides how this claim is
                   examined (MASTER_PLAN §2.4). Leading with a video CTA on a
                   claim routed to a site visit invites the adjuster to do the
                   wrong thing, and on a settled claim it invites them to do
                   anything at all. */
                const mode = claim.assessmentMode ?? null;
                const settled =
                  claim.status === 'APPROVED' ||
                  claim.status === 'REJECTED' ||
                  claim.status === 'CLOSED';
                const videoIsTheMethod = !mode || mode === AssessmentMode.VIDEO;

                const explanation: Record<string, string> = {
                  [AssessmentMode.DESK_REVIEW]:
                    'Assessed on the documents submitted. No interview required.',
                  [AssessmentMode.SITE_VISIT]:
                    'Assessed by physical inspection at the risk address.',
                  [AssessmentMode.EXPERT_REFERRAL]:
                    'Referred to a claims expert for assessment.',
                };

                return (
                  <Card>
                    <CardHeader>
                      <CardTitle>Assessment</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {mode && (
                          <Badge variant="outline" className="w-fit">
                            {ASSESSMENT_MODE_LABELS[mode]}
                          </Badge>
                        )}

                        {videoIsTheMethod && !settled && (
                          <>
                            <Button
                              className="w-full"
                              onClick={handleStartVideoAssessment}
                              disabled={createVideoRoom.isPending || isNotifying}
                            >
                              <Video className="h-4 w-4 mr-2" />
                              {createVideoRoom.isPending || isNotifying
                                ? 'Starting...'
                                : 'Start Live Session'}
                            </Button>

                            <Button
                              className="w-full"
                              variant="outline"
                              onClick={() => navigate(`/claims/${claimId}/upload-video`)}
                            >
                              <Upload className="h-4 w-4 mr-2" />
                              Manual Upload
                            </Button>

                            <p className="text-xs text-muted-foreground text-center mt-5">
                              Creates room and notifies claimant via SMS
                            </p>

                            <ScheduleAssessment
                              claimId={claimId}
                              mode={mode}
                              scheduledFor={claim.scheduledAssessmentTime}
                              onScheduled={() => refetchClaim()}
                            />
                          </>
                        )}

                        {!videoIsTheMethod && !settled && (
                          <>
                            <p className="text-sm text-muted-foreground">
                              {explanation[mode!] ?? 'No interview required for this assessment mode.'}
                            </p>
                            <ScheduleAssessment
                              claimId={claimId}
                              mode={mode}
                              scheduledFor={claim.scheduledAssessmentTime}
                              onScheduled={() => refetchClaim()}
                            />
                          </>
                        )}

                        {settled && (
                          <p className="text-sm text-muted-foreground">
                            This claim is {claim.status.toLowerCase()}. The assessment is complete.
                          </p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })()}

            {/* What the visit found — only on a claim routed to the ground. */}
            {canManageSessions && claim.assessmentMode === AssessmentMode.SITE_VISIT && (
              <SiteVisitCard
                claimId={claimId || ''}
                settled={
                  claim.status === 'APPROVED' ||
                  claim.status === 'REJECTED' ||
                  claim.status === 'CLOSED'
                }
              />
            )}

            {/* Claimant Info */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Claimant
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  <Avatar>
                    <AvatarFallback>
                      {getInitials(claim.claimant?.fullName || claim.claimantId)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium text-sm">{claim.claimant?.fullName || 'Unknown'}</p>
                    <p className="text-xs text-muted-foreground">
                      {claim.claimant?.phoneNumber || 'No phone'}{' '}
                      {claim.nric && (
                        <span>
                          / {claim.nric}{' '}
                          {!canViewUnmaskedPII && (
                            <Lock className="h-2 w-2 inline ml-1 mb-1 text-muted-foreground/50" />
                          )}
                        </span>
                      )}
                    </p>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {claim.claimant?.kycStatus === 'VERIFIED' ? (
                        <Badge variant="success" className="text-[10px] w-fit">
                          eKYC Verified
                        </Badge>
                      ) : (
                        <Badge variant="warning" className="text-[10px] w-fit">
                          KYC {claim.claimant?.kycStatus || 'PENDING'}
                        </Badge>
                      )}
                      {claim.consent?.claimProcessing ? (
                        <Badge
                          variant="info"
                          className="text-[10px] w-fit bg-blue-100 text-blue-700 hover:bg-blue-100 border-none"
                        >
                          PDPA Consented
                        </Badge>
                      ) : (
                        <Badge variant="destructive" className="text-[10px] w-fit">
                          PDPA PENDING
                        </Badge>
                      )}
                    </div>

                    {/* A gate nobody can satisfy is a gate that gets switched
                        off. Automated eKYC is not integrated, so the operator
                        records what they examined and the server keeps their
                        name against it. */}
                    <IdentityControl
                      claimantId={claim.claimant?.id}
                      kycStatus={claim.claimant?.kycStatus}
                      onVerified={() => refetchClaim()}
                    />
                  </div>
                </div>
                {claim.siuInvestigatorId && canInvestigateSIU && (
                  <div className="flex items-center gap-2 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                    <AlertTriangle className="h-4 w-4 text-amber-600" />
                    <span className="text-xs font-medium text-amber-700">Escalated to SIU</span>
                  </div>
                )}

                {latestClaimantClientInfo && (
                  <div className="pt-2 space-y-4">
                    {latestClaimantClientInfo.latitude && latestClaimantClientInfo.longitude && (
                      <div className="h-52 w-full rounded-lg overflow-hidden border bg-muted/20">
                        <iframe
                          width="100%"
                          height="100%"
                          frameBorder="0"
                          style={{ border: 0 }}
                          src={`https://maps.google.com/maps?q=${latestClaimantClientInfo.latitude},${latestClaimantClientInfo.longitude}&z=14&output=embed`}
                          allowFullScreen
                          title="Claimant Location"
                        ></iframe>
                      </div>
                    )}

                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between gap-2">
                        <span className="text-muted-foreground">Location:</span>
                        <InfoTooltip
                          content={locationData?.displayName || 'Unknown'}
                          direction="top"
                          className="max-w-[75%]"
                          trigger={
                            <span className="font-medium text-right truncate">
                              {isLocationLoading ? (
                                <span className="flex items-center gap-1 justify-end">
                                  <Loader2 className="h-2 w-2 animate-spin" />
                                  Resolving...
                                </span>
                              ) : (
                                locationData?.displayName || 'Unknown'
                              )}
                            </span>
                          }
                        />
                      </div>
                      <div className="flex justify-between gap-2">
                        <span className="text-muted-foreground">ISP:</span>
                        <span className="font-medium text-right">
                          {(() => {
                            const ispParts = [
                              latestClaimantClientInfo.isp,
                              latestClaimantClientInfo.organisation,
                              latestClaimantClientInfo.region,
                              latestClaimantClientInfo.postalCode,
                              latestClaimantClientInfo.city,
                              latestClaimantClientInfo.country,
                            ].filter(Boolean);
                            return ispParts.length > 0 ? ispParts.join(', ') : 'N/A';
                          })()}
                        </span>
                      </div>
                      <div className="flex justify-between gap-2">
                        <span className="text-muted-foreground">IP Address:</span>
                        <span className="font-medium text-right">
                          {latestClaimantClientInfo.ipv4 || latestClaimantClientInfo.ipv6 || 'N/A'}
                        </span>
                      </div>
                      <div className="flex justify-between gap-2">
                        <span className="text-muted-foreground">Device:</span>
                        <span className="font-medium text-right truncate max-w-[140px]">
                          {latestClaimantClientInfo.browser} on {latestClaimantClientInfo.platform}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Category-aware detail panel: Vehicle (motor) or Property (flood) */}
            {claim.category === 'MOTOR' && (
              <Card>
                <CardHeader className="py-4">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Car className="h-4 w-4" />
                    Vehicle Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <div className="flex flex-col sm:flex-row justify-between">
                    <span className="text-muted-foreground">Plate No:</span>
                    <span className="font-medium">{claim.vehiclePlateNumber || 'N/A'}</span>
                  </div>
                  <div className="flex flex-col sm:flex-row justify-between">
                    <span className="text-muted-foreground">Chassis No:</span>
                    <span className="font-medium text-uppercase">
                      {claim.vehicleChassisNumber || 'N/A'}
                    </span>
                  </div>
                  <div className="flex flex-col sm:flex-row justify-between">
                    <span className="text-muted-foreground">Engine No:</span>
                    <span className="font-medium text-uppercase">
                      {claim.vehicleEngineNumber || 'N/A'}
                    </span>
                  </div>
                  <div className="flex flex-col sm:flex-row justify-between">
                    <span className="text-muted-foreground">Make/Model:</span>
                    <span className="font-medium">
                      {claim.vehicleMake} {claim.vehicleModel}
                      {!claim.vehicleMake && !claim.vehicleModel && 'N/A'}
                    </span>
                  </div>
                  <div className="flex flex-col sm:flex-row justify-between">
                    <span className="text-muted-foreground">Year:</span>
                    <span className="font-medium">{claim.vehicleYear || 'N/A'}</span>
                  </div>
                </CardContent>
              </Card>
            )}
            {claim.category === 'FLOOD' && <PropertyDetailsPanel claim={claim} />}

            {/* Non-motor value-add cards. The evidence checklist + fraud
                signals also benefit motor claims once their requirement
                rows are seeded, but for now the panels stay flood-only. */}
            {claim.category !== 'MOTOR' && (
              <>
                <EvidenceChecklistCard claimId={claim.id} />
                <FraudSignalsCard claimId={claim.id} />
              </>
            )}

            {/* Policy Information */}
            <Card>
              <CardHeader className="py-4">
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Policy Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="flex flex-col sm:flex-row justify-between">
                  <span className="text-muted-foreground">Policy No:</span>
                  <span className="font-medium">{claim.policyNumber || 'N/A'}</span>
                </div>
                <div className="flex flex-col sm:flex-row justify-between">
                  <span className="text-muted-foreground">Policy Type:</span>
                  <span className="font-medium text-uppercase">
                    {claim.category === 'MOTOR'
                      ? convertToTitleCase(claim.claimType ?? 'Motor')
                      : getCategoryConfig(claim.category).label}
                  </span>
                </div>
                <div className="flex flex-col sm:flex-row justify-between">
                  <span className="text-muted-foreground">Sum Insured:</span>
                  <span className="font-medium text-uppercase">
                    {claim.sumInsured ? `RM ${claim.sumInsured.toLocaleString()}` : 'N/A'}
                  </span>
                </div>
                {/* No-claim discount and panel workshops are motor concepts.
                    On a fire or travel claim they read as an unfinished port of
                    a motor system — and this platform's scope is non-motor, so
                    the fields are shown only where they mean something. */}
                {claim.category === 'MOTOR' && (
                  <>
                    <div className="flex flex-col sm:flex-row justify-between">
                      <span className="text-muted-foreground">NCD Rate:</span>
                      <span className="font-medium">
                        {claim.ncdRate ? `${(claim.ncdRate * 100).toFixed(0)}%` : 'N/A'}
                      </span>
                    </div>
                    <div className="flex flex-col sm:flex-row justify-between">
                      <span className="text-muted-foreground">Panel Workshop:</span>
                      <span className="font-medium">{claim.workshopName || 'Not Assigned'}</span>
                    </div>
                  </>
                )}

                {/* What a non-motor reviewer needs in their place. The excess
                    is the figure they check the quantum against, and it is on
                    the claim itself — the policy is referenced by number only,
                    so anything drawn from the policy record would never render
                    here. */}
                {claim.category !== 'MOTOR' && (
                  <div className="flex flex-col sm:flex-row justify-between">
                    <span className="text-muted-foreground">Excess:</span>
                    <span className="font-medium">
                      {claim.excessAmount ? `RM ${claim.excessAmount.toLocaleString()}` : 'Nil'}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <ConfirmationDialog
        open={isTrinityConfirmOpen}
        onOpenChange={setIsTrinityConfirmOpen}
        title="Rerun Trinity Analysis"
        description={
          <div className="space-y-3 pt-1">
            <p className="text-sm text-muted-foreground">
              This will rerun AI extraction and cross-check analysis for the following documents in
              this claim. This process may take a minute. Are you sure you want to proceed?
            </p>
            <ul className="list-disc list-inside space-y-1">
              {claim.documents
                ?.filter((doc: any) => NORMALIZABLE_TYPES.includes(doc.type))
                .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
                .map((doc: any) => (
                  <li key={doc.id} className="text-xs text-muted-foreground font-medium">
                    {doc.filename || doc.name}
                  </li>
                ))}
            </ul>
          </div>
        }
        confirmText="Confirm"
        onConfirm={handleTriggerTrinity}
        isLoading={triggerTrinity.isPending}
      />

      <ConfirmationDialog
        open={isReplaceConfirmOpen}
        onOpenChange={open => {
          setIsReplaceConfirmOpen(open);
          if (!open) {
            setPendingFile(null);
            setSelectedDocForReplace(null);
          }
        }}
        title="Replace Document"
        description={
          <div className="space-y-4 pt-2">
            <p className="text-sm text-muted-foreground">
              Are you sure you want to replace <b>{selectedDocForReplace?.filename}</b>? This will
              rerun the AI extraction and cross-check analysis.
            </p>

            <div
              className={cn(
                'border border-dashed rounded-lg p-3 flex items-center justify-between transition-colors bg-muted/20 hover:bg-muted/40',
                pendingFile ? 'border-solid border-primary/50 bg-primary/5' : 'border-border'
              )}
            >
              <div className="flex items-center gap-3 overflow-hidden">
                <div className="bg-primary/10 p-2 rounded-full text-primary shrink-0">
                  <FileText size={18} />
                </div>
                {pendingFile ? (
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{pendingFile.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {(pendingFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                ) : (
                  <div className="text-sm text-muted-foreground">
                    <p>Upload New</p>
                  </div>
                )}
              </div>
              {pendingFile ? (
                <button
                  onClick={() => {
                    setTimeout(() => {
                      setPendingFile(null);
                    }, 500);
                  }}
                  className="p-1.5 hover:bg-destructive/10 text-muted-foreground hover:text-destructive rounded-full transition-colors"
                >
                  <X size={16} />
                </button>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 text-xs font-semibold text-primary bg-primary/10 hover:bg-primary hover:text-primary-foreground transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                >
                  Browse
                </Button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf, image/*"
                className="hidden"
                onChange={handleFileSelect}
              />
            </div>
          </div>
        }
        confirmText="Confirm"
        onConfirm={handleConfirmReplace}
        isLoading={replaceDocument.isPending}
        disabledConfirm={!pendingFile}
      />
    </div>
  );
}
